// src/components/GardenVisualisation.tsx
/**
 * Component responsible for loading and displaying existing gardens in the
 * 'Suntap' app. Fetches garden data for a specific pub ID,
 * renders garden polygons on the 3D scene, and handles garden selection.
 * Manages state for visible and selected gardens, allowing users to
 * visualise and interact with previously defined outdoor spaces.
 */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import * as THREE from 'three';
import { Box } from '@mui/material';

import { Garden, GardenBaseProps, GardenVisualState } from '../../types/gardenTypes';
import { getPubGardens } from '../../API'; // Assuming this fetches garden data now
import { disposeObject3D, convertToInternalCoords, convertFromExternalCoords, createTextSprite } from '../../utils/threeUtils';
import { createPolygonVisualization } from '../../utils/gardenShapeUtils';

export interface GardenVisualisationProps extends GardenBaseProps {
  scene: THREE.Scene; // Make required
  container: HTMLElement; // Make required
  trueUpVector: THREE.Vector3; // Make required
  localOrigin: THREE.Vector3; // Add this line
  googlePlaceId?: string; // Keep optional
  reload?: number; // Counter to trigger garden reload
  onGardensLoaded?: (gardens: Garden[]) => void;
  onGardenSelect?: (garden: Garden) => void; // Callback when a garden polygon is clicked
  onGardenDeselect?: () => void; // New callback for when gardens are deselected
  gardenBeingAdjustedUid?: string | null; // <<< ADDED: UID of garden in elevation edit
}

const GardenVisualisation: React.FC<GardenVisualisationProps> = ({
  googlePlaceId,
  scene,
  container,
  trueUpVector,
  localOrigin, // Add this line
  reload = 0,
  onGardenSelect,
  onGardenDeselect,
  onGardensLoaded,
  gardenBeingAdjustedUid = null
}) => {
  // Ref to the group containing all *loaded* garden visualisations
  const gardensGroupRef = useRef<THREE.Group | null>(null);
  // Store refs to created objects for easy clearing AND potential direct manipulation (though currently redrawing)
  const gardenObjectsRef = useRef<Record<string, { group: THREE.Group, elements: THREE.Object3D[] }>>({}); // Store group and elements

  // Visual state - use the expected GardenVisualState structure
  const [visualState, setVisualState] = useState<GardenVisualState>({
    selectedGarden: null, // Store the selected Garden object
    visibleGardens: [], // Store the raw Garden data loaded from API
    hoveredGarden: null // Kept for potential future use
  });

  // Function to clear previously visualised gardens
  const clearGardenVisuals = useCallback(() => {
    // First remove sprites that were added directly to the scene
    Object.values(gardenObjectsRef.current).forEach(item => {
      // Check if this garden group has a nameSprite reference in its userData
      if (item.group.userData && item.group.userData.nameSprite) {
        const sprite = item.group.userData.nameSprite;
        // Remove sprite from scene if it's still there
        if (sprite && sprite.parent) {
          sprite.parent.remove(sprite);
        }
        // Dispose the sprite material
        if (sprite && sprite.material) {
          const material = sprite.material as THREE.SpriteMaterial;
          if (material.map) material.map.dispose();
          material.dispose();
        }
      }
    });

    // Then dispose objects tracked in the ref by removing the group
    Object.values(gardenObjectsRef.current).forEach(item => {
      disposeObject3D(item.group); // Dispose the group, which should handle children
    });
    gardenObjectsRef.current = {}; // Reset tracked objects map

    // Also ensure the main container group is empty, though disposeObject3D should handle removal
    if (gardensGroupRef.current) {
      while (gardensGroupRef.current.children.length > 0) {
        // This might be redundant if disposeObject3D works correctly, but belt-and-suspenders
        gardensGroupRef.current.remove(gardensGroupRef.current.children[0]);
      }
    }
  }, []);

  // Create visuals for a single garden
  const createGardenVisual = useCallback((
    garden: Garden,
    isSelected: boolean = false // Use this flag for highlighting
  ) => {
    if (!gardensGroupRef.current || !container) {
      console.warn("Cannot create garden visual: Missing group or container");
      return null; // Return null instead of empty array
    }
  
    if (!garden.garden_latlon || garden.garden_latlon.length < 3) {
      console.warn(`Garden ${garden.name || garden.uid} has insufficient coordinates.`);
      return null;
    }
  
    try {
      // Extract the elevation from the garden object
      const gardenElevation = garden.elevation !== undefined ? garden.elevation : 0;
      // console.log(`Garden ${garden.name || garden.uid} using elevation: ${gardenElevation}m`);
  
      // Convert from lat/lon to ABSOLUTE Internal world coordinates
      const worldPoints = garden.garden_latlon.map(coords => {
        const lat = coords[0];
        const lon = coords[1];
        
        // Ensure we're passing the garden elevation to each point
        const ecefCoords = convertFromExternalCoords([lat, lon, gardenElevation]);
        return convertToInternalCoords([ecefCoords.x, ecefCoords.y, ecefCoords.z]);
      });
  
      // --- CORE FIX: Shift absolute points to the viewer's local space ---
      const shiftedWorldPoints = worldPoints.map(p => p.clone().sub(localOrigin));
  
      // Use first point as local origin for better numerical stability
      const localOriginForPolygon = shiftedWorldPoints.length > 0 ? shiftedWorldPoints[0].clone() : null;
  
      // Create the visual representation using the shared utility
      const { group, elements } = createPolygonVisualization(
        shiftedWorldPoints, // Use the shifted points
        container,
        localOriginForPolygon,
        {
          // Use orange for non-selected, blue for selected
          fillColor: isSelected ? 0x4488ff : 0xFFA500, // Blue if selected, else Orange
          fillOpacity: isSelected ? 0.7 : 0.5, // More opaque if selected
          outlineColor: isSelected ? 0x3366ff : 0xFF8C00, // Darker Blue if selected, else Dark Orange
          outlineWidth: isSelected ? 3.0 : 2.5, // Slightly thicker if selected
          vertexColor: 0xffff00, // Yellow vertices always
          vertexSize: 0.25,
          renderOrderOffset: isSelected ? 10 : 0, // Render selected slightly on top if needed
          isSelected: isSelected, // Pass flag for internal logic too if needed by util
          showVertices: true,
          useLocalCoords: true
        }
      );
  
      if (group) {
        // Set up the group
        group.name = `garden_group_${garden.uid}`;
        // Store critical info in userData for raycasting/identification
        group.userData = {
          gardenId: garden.uid,
          type: 'garden_visual', // Identify this as a clickable garden visual
          interactive: true, // Flag for potential interaction systems
          garden: garden // Store reference to the full garden data
        };
  
        // Also add userData to individual elements for robust raycasting
        elements.forEach(element => {
          element.userData = { ...group.userData }; // Copy userData
        });
  
        // Add name text sprite above the garden
        // --- CORE FIX: Calculate center from SHIFTED points for correct label placement ---
        const center = new THREE.Vector3();
        shiftedWorldPoints.forEach(point => center.add(point));
        center.divideScalar(shiftedWorldPoints.length);
        
        // Create text sprite with ONLY the garden name (fallback to UID if no name)
        // Ensure we're only showing the garden name, not including the pub name
        let displayName = garden.name || garden.uid || "Garden";

        // In case the garden name includes the pub name, extract just the subarea name
        // This handles possible formats like "PubName - GardenName" or similar
        if (displayName.includes(' - ')) {
          // Extract the part after the hyphen which should be the garden name
          displayName = displayName.split(' - ').pop() || displayName;
        }

        // Create the sprite with just the garden/subarea name
        const nameSprite = createTextSprite(displayName);
        
        // Position the sprite above the garden center
        nameSprite.position.copy(center).addScaledVector(trueUpVector, 3); // Position 3m above the garden center to ensure visibility above garden elements
        
        // IMPORTANT: Add sprite directly to the scene like the pub name
        // Instead of adding to the group
        scene.add(nameSprite);
        
        // Store reference to the sprite in the garden's userData for cleanup
        group.userData.nameSprite = nameSprite;
  
        // Add the group containing fill/outline/vertices to the main gardens group
        gardensGroupRef.current.add(group);
  
        // Return the created group and elements for tracking
        return { group, elements };
      }
  
      return null; // Return null if group creation failed
    } catch (error) {
      console.error(`Error creating visuals for garden ${garden.name || garden.uid}:`, error);
      return null;
    }
  }, [container, trueUpVector, scene, localOrigin]); // Add localOrigin to dependencies

    // Effect to re-render visuals when selection changes
    useEffect(() => {
      // Ensure the group exists and we have gardens loaded
      if (!gardensGroupRef.current || !container) { // Also check container
          return;
      }
  
      // Clear existing visuals before re-rendering
      clearGardenVisuals();
  
      // Re-create visuals for all loaded gardens, highlighting the selected one,
      // and skipping the one being adjusted.
      const newTrackedObjects: Record<string, { group: THREE.Group, elements: THREE.Object3D[] }> = {};
      visualState.visibleGardens.forEach(garden => {
          // <<< ADDED: Check if this garden is the one being adjusted elsewhere
          if (garden.uid === gardenBeingAdjustedUid) {
              // console.log(`Skipping render for adjusting garden: ${garden.uid}`);
              return; // Skip rendering this garden entirely
          }
  
          // Check if the current garden is the selected one
          const isSelected = !!visualState.selectedGarden && garden.uid === visualState.selectedGarden.uid;
          const visualComponents = createGardenVisual(garden, isSelected);
          if (visualComponents) {
              newTrackedObjects[garden.uid] = visualComponents;
          } else {
              console.warn(`Failed to re-create visual objects for garden ${garden.name || garden.uid} during update.`);
          }
      });
  
      gardenObjectsRef.current = newTrackedObjects; // Update the tracked objects ref
  
    // <<< ADDED: gardenBeingAdjustedUid to dependency array
    }, [visualState.selectedGarden, visualState.visibleGardens, clearGardenVisuals, createGardenVisual, gardenBeingAdjustedUid, container]);


  // Effect to fetch and visualise garden data when googlePlaceId changes or reload counter increments
  useEffect(() => {
    // Setup group if it doesn't exist
    if (!gardensGroupRef.current && scene) {
        gardensGroupRef.current = new THREE.Group();
        gardensGroupRef.current.name = 'GardensVisualisationGroup';
        scene.add(gardensGroupRef.current);
    }

    // Track API request to prevent duplicate calls if googlePlaceId changes rapidly
    let isFetching = false;
    let isMounted = true;

    const fetchAndVisualiseGardens = async () => {
      // Reset state (including selection) when googlePlaceId changes or reload is triggered
      setVisualState({ selectedGarden: null, visibleGardens: [], hoveredGarden: null });

      if (!googlePlaceId || !scene || !gardensGroupRef.current || !container) {
        if (isMounted) {
            clearGardenVisuals(); // Clear any old visuals if googlePlaceId becomes null/invalid
            if (onGardensLoaded) onGardensLoaded([]);
        }
        return;
      }
      if (isFetching) {
           return;
      }

      isFetching = true;
      clearGardenVisuals(); // Clear previous pub's gardens immediately

      try {
        // console.log(`Fetching gardens for pub ${googlePlaceId}`, reload ? `(reload #${reload})` : '');
        // Add a small delay before fetching to ensure database updates are complete
        if (reload > 0) {
          await new Promise(resolve => setTimeout(resolve, 200));
        }
        
        const response = await getPubGardens(googlePlaceId); // Assuming getLatLon fetches garden data

        if (!isMounted) { isFetching = false; return; }

        let gardens: Garden[] = [];
        // Adapt based on actual API response structure
        if (response.data && typeof response.data === 'object') {
             // If data is an object where values are gardens (e.g., keyed by UID)
             if (!Array.isArray(response.data)) {
                 // Explicitly type 'item' as 'unknown' for the filter type guard
                 gardens = Object.values(response.data).filter((item: unknown): item is Garden =>
                    !!item && typeof item === 'object' && 'uid' in item && 'garden_latlon' in item && Array.isArray(item.garden_latlon)
                 );
             }
             // If data is directly an array of gardens
             else if (Array.isArray(response.data)) {
                  // Explicitly type 'item' as 'unknown' for the filter type guard
                  gardens = response.data.filter((item: unknown): item is Garden =>
                    !!item && typeof item === 'object' && 'uid' in item && 'garden_latlon' in item && Array.isArray(item.garden_latlon)
                 );
             }
        }

        if (gardens.length > 0) {
            // console.log(`Loaded ${gardens.length} gardens for pub ${googlePlaceId}:`);
            // gardens.forEach(garden => {
            //     console.log(`- Garden "${garden.name || garden.uid}" elevation: ${garden.elevation !== undefined ? garden.elevation : 'undefined'}`);
            // });
            
            // Store the raw garden data. Visuals will be created by the selection effect.
            // Reset selectedGarden when new data is loaded.
            setVisualState(prev => ({ ...prev, visibleGardens: gardens, selectedGarden: null }));

            if (onGardensLoaded) {
                onGardensLoaded(gardens);
            }
        } else {
          console.log(`No gardens found for pub ${googlePlaceId}`);
          // Reset state fully
          setVisualState({ selectedGarden: null, visibleGardens: [], hoveredGarden: null });
          if (onGardensLoaded) {
                 onGardensLoaded([]);
             }
        }
      } catch (error) {
        console.error(`Error fetching gardens for pub ${googlePlaceId}:`, error);
        if (isMounted) {
            // Reset state fully on error
            setVisualState({ selectedGarden: null, visibleGardens: [], hoveredGarden: null });
            if (onGardensLoaded) {
                onGardensLoaded([]);
            }
        }
      } finally {
        if (isMounted) {
            isFetching = false;
        }
      }
    };

    fetchAndVisualiseGardens();

    // Cleanup group on unmount
    const group = gardensGroupRef.current; // Capture ref for cleanup closure
    return () => {
      isMounted = false;
      if (group) {
         clearGardenVisuals(); // Use the cleanup function
        // Remove main group from scene
        if (group.parent) {
          group.parent.remove(group);
        }
      }
      gardensGroupRef.current = null;
    };
    // This effect now runs when googlePlaceId changes, reload counter increments, or scene/container refs change
  }, [googlePlaceId, scene, container, onGardensLoaded, clearGardenVisuals, reload]); // Added reload dependency


  // Handler for when a garden is clicked in the scene (using Raycaster)
  const handleGardenClicked = useCallback((gardenId: string) => {
    const clickedGarden = visualState.visibleGardens.find(g => g.uid === gardenId);
    if (clickedGarden) {
      // Update visual state to trigger re-render with new selection
      // Store the actual Garden object in the state
      setVisualState(prev => ({
        ...prev,
        selectedGarden: clickedGarden // Set the selected Garden object
      }));

      // Call external handler AFTER updating state
      if (onGardenSelect) {
        onGardenSelect(clickedGarden);
      }
    } else {
        console.warn(`GardenVisualisation: Clicked garden with ID ${gardenId} not found in visible gardens.`);
    }
  }, [visualState.visibleGardens, onGardenSelect]); // Depends on garden list and callback

  // Set up raycasting for garden selection
  useEffect(() => {
    if (!container || !gardensGroupRef.current) {
        // console.log("Raycasting setup skipped: no container or gardens group.");
        return;
    }

    const raycaster = new THREE.Raycaster();
    const mouse = new THREE.Vector2();
    let camera = scene?.userData?.camera as THREE.PerspectiveCamera | undefined; // Get camera from scene

    const handleClick = (event: MouseEvent) => {
        camera = scene?.userData?.camera as THREE.PerspectiveCamera | undefined; // Update camera reference just in case
        if (!gardensGroupRef.current || !camera || visualState.visibleGardens.length === 0) {
           // console.log("Raycasting click skipped: no group, camera, or visible gardens.");
           return; // Don't raycast if nothing to hit or no camera
        }

        // Calculate mouse position in normalized device coordinates (-1 to +1)
        const rect = container.getBoundingClientRect();
        mouse.x = ((event.clientX - rect.left) / rect.width) * 2 - 1;
        mouse.y = -((event.clientY - rect.top) / rect.height) * 2 + 1;

        // Update the picking ray with the camera and mouse position
        raycaster.setFromCamera(mouse, camera);

        // Find intersections specifically with the children of the gardens group
        // Pass `true` to check descendants (fill, outline, vertices)
        const intersects = raycaster.intersectObjects(gardensGroupRef.current.children, true);

        // Process the first intersection that has our 'garden_visual' type userData
        for (const intersection of intersects) {
            // Traverse up from the intersected object (could be fill/outline/vertex)
            // until we find the object with the correct userData (the group or element)
            let intersectedObject: THREE.Object3D | null = intersection.object;
            while (intersectedObject) {
                 // Check if userData exists and has the expected properties
                 if (intersectedObject.userData && intersectedObject.userData.type === 'garden_visual' && typeof intersectedObject.userData.gardenId === 'string') {
                    handleGardenClicked(intersectedObject.userData.gardenId);
                    return; // Stop after handling the first valid garden click
                 }
                 intersectedObject = intersectedObject.parent; // Move up the hierarchy
            }
        }
        // If click didn't hit any garden visual, deselect the currently selected one
        if (intersects.length === 0 && visualState.selectedGarden !== null) {
             setVisualState(prev => ({ ...prev, selectedGarden: null }));
             // Call the deselect callback when clicking away from gardens
             if (onGardenDeselect) {
                 onGardenDeselect();
             }
        }
    };

    // Use capture phase to potentially intercept click before OrbitControls panning starts
    container.addEventListener('click', handleClick, false);

    return () => {
      container.removeEventListener('click', handleClick, false);
    };
    // Re-add listener if container/scene/handler changes or gardens appear/disappear or selection changes (for deselection logic)
  }, [container, scene, handleGardenClicked, visualState.visibleGardens.length, visualState.selectedGarden, onGardenSelect, onGardenDeselect]);

  // This component primarily manages state and THREE objects, doesn't render direct UI
  return null;
};

export default GardenVisualisation;